import React, { useEffect, useMemo, useState } from "react";
import {
  Sparkles, Send, X, Check, Loader2, Mail, Linkedin, Hash, HardHat,
  RefreshCw, ChevronDown, ChevronRight, Copy, AlertTriangle, CheckCircle2,
  Edit2, MessageSquare, Zap, Clock,
} from "lucide-react";
import { toast } from "sonner";

import ConsoleShell from "@/components/ConsoleShell";
import api from "@/lib/api";

const KIND_META = {
  email_prospect: { label: "Email prospect", icon: Mail, color: "#10B981" },
  linkedin_message: { label: "Message LinkedIn", icon: Linkedin, color: "#0A66C2" },
  social_post: { label: "Post réseau social", icon: Hash, color: "#EC4899" },
  chantix_review: { label: "Brief chantiers", icon: HardHat, color: "#F59E0B" },
  report: { label: "Rapport", icon: MessageSquare, color: "#A78BFA" },
};

export default function Mission() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const refresh = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/mission/today");
      setData(d);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data: r } = await api.post("/mission/run-now");
      if (r.skipped) {
        toast.info("Le batch du jour est déjà préparé.");
      } else {
        toast.success(`Batch prêt : ${r.counts.total} tâches`);
      }
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la préparation");
    } finally {
      setRunning(false);
    }
  };

  const pending = useMemo(
    () => (data?.tasks || []).filter((t) => t.status === "pending"),
    [data],
  );
  const done = useMemo(
    () => (data?.tasks || []).filter((t) => t.status === "done"),
    [data],
  );
  const rejected = useMemo(
    () => (data?.tasks || []).filter((t) => t.status === "rejected" || t.status === "failed"),
    [data],
  );

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === pending.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(pending.map((t) => t.id)));
    }
  };

  const batchApprove = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Approuver et exécuter ${selected.size} tâche(s) ?`)) return;
    try {
      const { data: r } = await api.post("/mission/tasks/batch-approve", {
        task_ids: Array.from(selected),
      });
      toast.success(`${r.succeeded}/${r.total} tâches exécutées`);
      setSelected(new Set());
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur batch");
    }
  };

  return (
    <ConsoleShell>
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-6">
        {/* Hero */}
        <div className="mb-8 border border-white/10 bg-gradient-to-br from-white/[0.04] to-transparent p-6 lg:p-8 relative overflow-hidden">
          <div className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.3em] text-zinc-600">
            Mission Control · {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
          </div>
          <div className="flex items-start gap-3 mb-4">
            <Sparkles className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" />
            <div>
              <h1 className="font-display text-2xl lg:text-4xl font-semibold tracking-tight">
                Bonjour. Voici ce que ton équipe a préparé.
              </h1>
              <p className="text-sm text-zinc-500 mt-2">
                Chaque matin à 06:30, CLARA, THIERRY et l&apos;équipe préparent les actions du jour.
                Tu valides en quelques clics — les agents exécutent.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-6 max-w-xl">
            <MetricBlock label="En attente" value={pending.length} accent="#F59E0B" testid="metric-pending" />
            <MetricBlock label="Validées" value={done.length} accent="#10B981" testid="metric-done" />
            <MetricBlock label="Rejetées" value={rejected.length} accent="#71717A" testid="metric-rejected" />
          </div>

          <div className="flex items-center gap-2 mt-6 flex-wrap">
            <button
              onClick={runNow}
              disabled={running}
              data-testid="mission-run-now"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-white text-black hover:bg-zinc-200 transition disabled:opacity-50"
            >
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              {running ? "Les agents travaillent…" : "Préparer maintenant"}
            </button>
            <button
              onClick={refresh}
              className="p-2 text-zinc-400 hover:text-white border border-white/10 hover:border-white/30 transition"
              title="Rafraîchir"
              data-testid="mission-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            {data?.last_batch && (
              <div className="text-[11px] text-zinc-600 font-mono ml-2">
                <Clock className="w-3 h-3 inline mr-1" />
                Dernier batch : {new Date(data.last_batch.created_at).toLocaleString("fr-FR")}
              </div>
            )}
          </div>
        </div>

        {/* Batch actions bar */}
        {pending.length > 0 && (
          <div className="sticky top-2 z-10 mb-4 flex items-center gap-3 flex-wrap border border-white/10 bg-black/90 backdrop-blur-md px-4 py-2.5">
            <button
              onClick={selectAll}
              data-testid="mission-select-all"
              className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1.5"
            >
              <input
                type="checkbox"
                readOnly
                checked={selected.size === pending.length && pending.length > 0}
                className="accent-white"
              />
              {selected.size === pending.length ? "Tout désélectionner" : "Tout sélectionner"}
            </button>
            <div className="text-xs text-zinc-500 ml-auto">
              {selected.size} / {pending.length} sélectionné(s)
            </div>
            <button
              onClick={batchApprove}
              disabled={selected.size === 0}
              data-testid="mission-batch-approve"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 transition disabled:opacity-40"
            >
              <Check className="w-3 h-3" />
              Approuver & exécuter ({selected.size})
            </button>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-600" /></div>
        ) : pending.length === 0 && done.length === 0 && rejected.length === 0 ? (
          <EmptyState onRun={runNow} running={running} />
        ) : (
          <>
            <Section title="À valider" tasks={pending} accent="#F59E0B" expandedDefault>
              {pending.map((t) => (
                <TaskRow
                  key={t.id}
                  task={t}
                  selected={selected.has(t.id)}
                  onSelect={() => toggleSelect(t.id)}
                  onChanged={refresh}
                />
              ))}
            </Section>
            {done.length > 0 && (
              <Section title="Exécutées aujourd'hui" tasks={done} accent="#10B981">
                {done.map((t) => (
                  <TaskRow key={t.id} task={t} onChanged={refresh} />
                ))}
              </Section>
            )}
            {rejected.length > 0 && (
              <Section title="Rejetées" tasks={rejected} accent="#71717A">
                {rejected.map((t) => (
                  <TaskRow key={t.id} task={t} onChanged={refresh} />
                ))}
              </Section>
            )}
          </>
        )}
      </div>
    </ConsoleShell>
  );
}

function MetricBlock({ label, value, accent, testid }) {
  return (
    <div className="border border-white/10 p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="font-display text-3xl font-semibold mt-1" style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}

function Section({ title, tasks, accent, children, expandedDefault = false }) {
  const [open, setOpen] = useState(expandedDefault);
  return (
    <div className="mb-4 border border-white/10">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
          <div className="text-sm font-medium" style={{ color: accent }}>{title}</div>
          <span className="text-xs text-zinc-500 font-mono">· {tasks.length}</span>
        </div>
      </button>
      {open && <div className="border-t border-white/10 divide-y divide-white/5">{children}</div>}
    </div>
  );
}

function TaskRow({ task, selected, onSelect, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const meta = KIND_META[task.kind] || KIND_META.report;
  const Icon = meta.icon;

  const approve = async (e) => {
    e?.stopPropagation();
    try {
      await api.post(`/mission/tasks/${task.id}/approve`);
      toast.success("Tâche exécutée");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec");
    }
  };

  const reject = async (e) => {
    e?.stopPropagation();
    try {
      await api.post(`/mission/tasks/${task.id}/reject`);
      toast.info("Tâche rejetée");
      onChanged();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const isPending = task.status === "pending";

  return (
    <div
      className={`px-4 py-3 transition ${selected ? "bg-white/[0.04]" : "hover:bg-white/[0.02]"}`}
      data-testid={`task-${task.id}`}
    >
      <div className="flex items-start gap-3">
        {isPending && onSelect && (
          <input
            type="checkbox"
            checked={selected}
            onChange={onSelect}
            className="mt-1 accent-white flex-shrink-0"
            data-testid={`task-checkbox-${task.id}`}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        <div
          className="w-8 h-8 grid place-items-center flex-shrink-0"
          style={{ backgroundColor: `${meta.color}15`, color: meta.color }}
        >
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <div className="text-sm font-medium truncate">{task.title}</div>
            <StatusPill status={task.status} />
          </div>
          <div className="text-xs text-zinc-500 line-clamp-2">{task.summary}</div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-[11px] text-zinc-500 hover:text-white mt-1 inline-flex items-center gap-1"
            data-testid={`task-expand-${task.id}`}
          >
            {expanded ? "Réduire" : "Voir le détail"} {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        </div>
        {isPending && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={approve}
              data-testid={`task-approve-${task.id}`}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 transition"
              title="Approuver et exécuter"
            >
              <Check className="w-3 h-3" /> Approuver
            </button>
            <button
              onClick={reject}
              data-testid={`task-reject-${task.id}`}
              className="p-1.5 text-zinc-500 hover:text-red-400 transition"
              title="Rejeter"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
      {expanded && <TaskDetail task={task} onChanged={onChanged} />}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: { label: "En attente", c: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
    done: { label: "Exécutée", c: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
    failed: { label: "Échec", c: "bg-red-500/10 text-red-400 border-red-500/20" },
    rejected: { label: "Rejetée", c: "bg-zinc-700/30 text-zinc-500 border-zinc-700/40" },
    approved: { label: "Approuvée", c: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  };
  const v = map[status] || map.pending;
  return (
    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${v.c}`}>
      {v.label}
    </span>
  );
}

function TaskDetail({ task, onChanged }) {
  if (task.kind === "email_prospect") {
    return <EmailTaskDetail task={task} onChanged={onChanged} />;
  }
  if (task.kind === "linkedin_message") {
    return <LinkedInTaskDetail task={task} />;
  }
  if (task.kind === "social_post") {
    return <SocialTaskDetail task={task} />;
  }
  if (task.kind === "chantix_review") {
    return <ChantixTaskDetail task={task} />;
  }
  return (
    <pre className="mt-3 text-xs text-zinc-500 bg-white/[0.02] p-3 overflow-x-auto">
      {JSON.stringify(task.payload, null, 2)}
    </pre>
  );
}

function EmailTaskDetail({ task, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(task.payload.subject || "");
  const [body, setBody] = useState(task.payload.body || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/mission/tasks/${task.id}`, {
        payload: { subject, body },
      });
      toast.success("Modifications enregistrées");
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error("Erreur sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 border border-white/5 bg-white/[0.02] p-3 space-y-2">
      <div className="grid grid-cols-[60px_1fr] gap-2 text-xs">
        <div className="text-zinc-500 uppercase tracking-wider">De</div>
        <div className="font-mono text-zinc-400">gabryx@chantix.pro</div>
        <div className="text-zinc-500 uppercase tracking-wider">À</div>
        <div className="font-mono text-zinc-200">{task.payload.to}</div>
      </div>
      {editing ? (
        <>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-white/30 outline-none"
            data-testid={`task-edit-subject-${task.id}`}
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-white/30 outline-none resize-y leading-relaxed"
            data-testid={`task-edit-body-${task.id}`}
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-xs border border-white/10 hover:bg-white/5 transition">
              Annuler
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="px-3 py-1.5 text-xs bg-white text-black hover:bg-zinc-200 transition disabled:opacity-50"
            >
              {saving ? "Sauvegarde…" : "Enregistrer"}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="text-sm font-medium text-zinc-100">{subject}</div>
          <div className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed select-text border-t border-white/5 pt-2">
            {body}
          </div>
          {task.status === "pending" && (
            <button
              onClick={() => setEditing(true)}
              className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1 mt-2"
              data-testid={`task-edit-${task.id}`}
            >
              <Edit2 className="w-3 h-3" /> Modifier avant envoi
            </button>
          )}
        </>
      )}
    </div>
  );
}

function LinkedInTaskDetail({ task }) {
  const text = task.payload.text || "";
  return (
    <div className="mt-3 border border-blue-500/10 bg-blue-500/[0.03] p-3 space-y-2">
      <div className="grid grid-cols-[80px_1fr] gap-2 text-xs">
        <div className="text-zinc-500 uppercase tracking-wider">À</div>
        <div className="text-zinc-200">{task.payload.to_name} {task.payload.company ? `· ${task.payload.company}` : ""}</div>
        {task.payload.linkedin_url && (
          <>
            <div className="text-zinc-500 uppercase tracking-wider">Profil</div>
            <a href={task.payload.linkedin_url} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline truncate">
              {task.payload.linkedin_url}
            </a>
          </>
        )}
      </div>
      <div className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed select-text border-t border-white/5 pt-2">
        {text}
      </div>
      <div className="flex items-center gap-2 pt-1">
        <CopyBtn text={text} />
        <span className="text-[10px] text-zinc-500">{text.length} / 280 caractères</span>
        {task.status === "pending" && (
          <span className="text-[10px] text-amber-400 ml-auto inline-flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Approuver = marquer envoyé manuellement
          </span>
        )}
      </div>
    </div>
  );
}

function SocialTaskDetail({ task }) {
  const { platform, caption, hashtags, visual_hint, video_script } = task.payload;
  return (
    <div className="mt-3 border border-pink-500/10 bg-pink-500/[0.03] p-3 space-y-2">
      <div className="text-[10px] uppercase tracking-[0.2em] text-pink-400">
        {platform} {visual_hint && `· 🎨 ${visual_hint}`}
      </div>
      <div className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed select-text">
        {caption}
      </div>
      {hashtags && (
        <div className="text-xs text-blue-400 font-mono">{hashtags}</div>
      )}
      {video_script && (
        <div className="border-t border-white/5 pt-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Script vidéo</div>
          <div className="text-xs text-zinc-300 whitespace-pre-wrap leading-relaxed select-text">{video_script}</div>
        </div>
      )}
      <div className="flex items-center gap-2 pt-1">
        <CopyBtn text={[caption, hashtags].filter(Boolean).join("\n\n")} />
        {task.status === "pending" && (
          <span className="text-[10px] text-amber-400 ml-auto inline-flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Approuver = marquer posté
          </span>
        )}
      </div>
    </div>
  );
}

function ChantixTaskDetail({ task }) {
  const level = task.payload.alert_level || "green";
  const colors = { red: "#EF4444", yellow: "#F59E0B", green: "#10B981" }[level] || "#10B981";
  return (
    <div className="mt-3 border border-white/5 bg-white/[0.02] p-3 space-y-2">
      <div className="flex items-center gap-2">
        <span
          className="text-[10px] uppercase tracking-wider px-2 py-0.5"
          style={{ backgroundColor: `${colors}20`, color: colors, border: `1px solid ${colors}40` }}
        >
          {level === "red" ? "Alerte" : level === "yellow" ? "Attention" : "Tout va bien"}
        </span>
        {task.payload.stats && (
          <span className="text-[10px] text-zinc-500 font-mono">
            {task.payload.stats.total} chantiers · {task.payload.stats.in_progress} en cours
          </span>
        )}
      </div>
      <div className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed select-text">{task.summary}</div>
      {task.payload.actions && (
        <div className="border-t border-white/5 pt-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">À faire aujourd&apos;hui</div>
          <div className="text-xs text-zinc-300 whitespace-pre-wrap leading-relaxed select-text">{task.payload.actions}</div>
        </div>
      )}
    </div>
  );
}

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Impossible de copier");
    }
  };
  return (
    <button
      onClick={copy}
      className={`inline-flex items-center gap-1 text-xs px-2 py-1 border transition ${
        copied ? "border-emerald-500/30 text-emerald-400" : "border-white/10 text-zinc-400 hover:text-white"
      }`}
    >
      {copied ? <CheckCircle2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? "Copié" : "Copier"}
    </button>
  );
}

function EmptyState({ onRun, running }) {
  return (
    <div className="text-center py-20 border border-dashed border-white/10">
      <Sparkles className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
      <div className="font-medium mb-2">Aucune tâche aujourd&apos;hui</div>
      <p className="text-sm text-zinc-500 max-w-md mx-auto mb-5">
        Le batch automatique tourne tous les jours à <span className="font-mono text-zinc-300">06:30</span> (Europe/Paris).
        <br />Tu peux aussi le lancer immédiatement pour tester.
      </p>
      <button
        onClick={onRun}
        disabled={running}
        data-testid="empty-run-btn"
        className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-white text-black hover:bg-zinc-200 transition disabled:opacity-50"
      >
        {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
        {running ? "Préparation…" : "Préparer maintenant"}
      </button>
    </div>
  );
}
